import { Set as ImmutableSet } from 'immutable';
import { Route } from 'react-router-dom';
import { connect } from 'react-redux';
import Moment from 'react-moment';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment-timezone';

import AnimatedRoutes, { absolute } from '../../components/AnimatedRoutes';
import { FloatingModal } from '../../components/Modal';
import {
  addActivity,
  removeActivity,
} from '../../../../ducks/event';
import { addNotification, removeNotification } from '../../../../ducks/notifications';
import { getImageUrl, navigate } from '../../../../helpers';
import Activity from './Activity';
import BackgroundImage from '../../components/BackgroundImage';
import Button from '../../components/Button';
import Content from '../../components/Content';
import Header from '../../components/Header';
import Maps from '../Maps';
import Subheader from '../../components/Subheader';
import TimeSimulator from '../../components/TimeSimulator';
import styles from './streamers.scss';
import withUser from '../../../../components/withUser';

const AbsoluteMap = absolute(Maps, true);

const propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    streamers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
    })),
    activities: PropTypes.arrayOf(PropTypes.shape()),
    timezone: PropTypes.string,
    shortcode: PropTypes.string,
    local_start_time: PropTypes.string,
    local_end_time: PropTypes.string,
  }),
  // dispatch: PropTypes.func.isRequired,
  onRequestOpenFn: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
    url: PropTypes.string,
    path: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool,
  personalActivities: PropTypes.instanceOf(ImmutableSet),
  notifications: PropTypes.objectOf(PropTypes.shape),
  dispatch: PropTypes.func.isRequired,
  debug: PropTypes.bool,
};

const hasPassed = (activity) => (
  ((activity.time_end && moment.utc(activity.time_end))
      || (moment.utc(activity.time).add(1, 'hours'))
  ).isBefore(moment().utc())
);

const isActive = (activity) => (
  moment.utc(activity.time).isBefore(moment().utc())
    && ((activity.time_end && moment.utc(activity.time_end))
      || (moment.utc(activity.time).add(1, 'hours'))
    ).isAfter(moment().utc())
);

class Streamers extends React.PureComponent {
  onAddActivity = (activity) => {
    const { dispatch, notifications } = this.props;
    dispatch(addActivity(activity.id));

    // If there is no existing notification, add one by default
    if (!notifications[activity.id]) {
      this.onAddNotification(activity, true);
    }
  }

  onRemoveActivity = (activity) => {
    const { dispatch, notifications } = this.props;
    dispatch(removeActivity(activity.id));

    // If there is an existing notification, remove it by default
    if (notifications[activity.id]) {
      this.onRemoveNotification(activity);
    }
  }

  onAddNotification = (activity, ignoreFail) => {
    const { dispatch } = this.props;
    return dispatch(addNotification({
      id: activity.id,
      type: 'activity',
      offset: 5,
      ignoreFail,
    }));
  }

  onRemoveNotification = (activity) => {
    const { dispatch } = this.props;
    return dispatch(removeNotification(activity.id));
  }

  onToggleNotification = (activity, ignoreFail) => {
    const { notifications } = this.props;
    if (notifications[activity.id]) {
      this.onRemoveNotification(activity);
    } else {
      this.onAddNotification(activity, ignoreFail);
    }
  }

  render() {
    const {
      event,
      loading,
      history,
      location,
      onRequestOpenFn,
      match,
      debug,
      personalActivities,
      notifications,
    } = this.props;

    const streamers = event && event.streamers
      .sort(
        (a, b) => a.game.localeCompare(b.game) || a.name.localeCompare(b.name),
      ).map((item) => ({
        ...item,
        schedule: event.activities.filter(
          (activity) => activity.title.toLowerCase() === item.name.toLowerCase(),
        ),
      }));

    const groupByGame = streamers && streamers.reduce(
      (acc, item) => ({ ...acc, [item.game]: [...acc[item.game] || [], item] }),
      {},
    );

    const localTimezone = moment.tz(moment.tz.guess()).zoneAbbr();

    return (
      <>
        <Header
          onMenuOpen={onRequestOpenFn}
          history={history}
          showUser
          loading={loading}
        >
          Streamers
        </Header>
        {event && debug
        && (
        <TimeSimulator
          start={event.local_start_time}
          end={event.local_end_time}
          timezone={event.timezone}
        />
        )}
        <Content>
          { Object.keys(groupByGame || {}).map((game) => (
            <React.Fragment key={game}>
              <Subheader>{game}</Subheader>
              <div className={styles.list}>
                { groupByGame[game].map((streamer) => {
                  const schedule = streamer.schedule.find((item) => !hasPassed(item));
                  const start = schedule && moment.parseZone(schedule.time);
                  const isNotSameTimeZone = start && (
                    start.utcOffset() !== start.tz(localTimezone).utcOffset()
                  );

                  return (
                    <div
                      className={styles.streamer}
                      key={streamer.id}
                      onClick={() => navigate(history, `${match.url}/${streamer.id}`)}
                      role="button"
                      tabIndex={0}
                    >
                      <BackgroundImage
                        className={styles.thumb}
                        key={streamer.id}
                        src={getImageUrl(streamer.image_id)}
                      />
                      <div className={styles.content}>
                        <div className={styles.name}>
                          {streamer.name}
                        </div>
                        { streamer.description
                          && (
                          <div className={styles.description}>
                            {streamer.description.length > 65 ? `${streamer.description.substring(0, 65)}...` : streamer.description}
                          </div>
                          )}
                        { schedule && (
                          <div className={styles.distance}>
                            {isActive(schedule) && (
                              <span className={styles.isActive}>
                                {'LIVE now! '}
                                {schedule.time_end && (
                                  <>(<Moment fromNow ago>{schedule.time_end}</Moment> left)</>
                                )}
                              </span>
                            )}
                            {!isActive(schedule) && (
                              <>
                                Next scheduled broadcast: <Moment format={isNotSameTimeZone ? `LT [${localTimezone || ''}] · dddd` : 'LT · dddd'}>{schedule.time}</Moment>
                                &nbsp;(<Moment fromNow>{schedule.time}</Moment>)
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          ))}
        </Content>
        <Route
          exact
          path={`${match.path}/:id`}
          render={(props) => {
            const streamer = props.match.params.id && streamers
              && streamers.find(
                (item) => item.id === props.match.params.id,
              );

            return (
              <FloatingModal
                isOpen={!!streamer}
                onRequestClose={() => navigate(history, match.url)}
              >
                { streamer
                  && (
                  <div>
                    <BackgroundImage
                      className={styles.activeBg}
                      src={getImageUrl(streamer.image_id)}
                    />
                    <div className={styles.activePartner}>
                      <div className={styles.activeTitle}>
                        {streamer.name}
                      </div>
                      <div className={styles.activeDescription}>
                        {streamer.description}
                      </div>

                      <div className={styles.activeActions}>
                        {
                          streamer.url
                          && (
                          <Button
                            outline
                            onClick={() => window.open(streamer.url)}
                          >
                            Visit the stream
                          </Button>
                          )
                        }
                      </div>
                      {streamer.schedule && (
                      <div className={styles.schedule}>
                        {streamer.schedule.map((item) => (
                          <Activity
                            key={item.id}
                            activity={item}
                            personalActivities={personalActivities}
                            hasNotification={!!notifications[item.id]}
                            isPersonal={personalActivities.has(item.id)}
                            onRemove={() => this.onRemoveActivity(item)}
                            onAdd={() => this.onAddActivity(item)}
                            onToggleNotification={
                              () => this.onToggleNotification(item)
                            }
                            hasPersonal={personalActivities.has(item.id)}
                            push={history.push}
                            shortcode={event.shortcode}
                            match={match}
                            localTimezone={localTimezone}
                            timezone={event.timezone}
                          />
                        ))}
                      </div>
                      )}
                    </div>
                  </div>
                  )}
              </FloatingModal>
            );
          }}
        />
        <AnimatedRoutes location={location}>
          <Route exact path={`${match.path}/map/:exhibitor`} render={(props) => (<AbsoluteMap {...props} />)} />
        </AnimatedRoutes>
      </>
    );
  }
}

Streamers.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    loading: state.getIn(['event', 'loading']),
    event: state.getIn(['event', 'data']),
    debug: state.getIn(['app', 'debug']),
    personalActivities: state.getIn(['event', 'activities']),
    notifications: state.getIn(['notifications', 'notifications']),
  };
}

export default withUser(connect(mapStateToProps)(Streamers));
